import classNames from 'classnames';
import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import { FC, memo, useCallback, useEffect, useMemo } from 'react';

import { UiButton } from 'src/components/UI/Button';
import { UiCleanInput } from 'src/components/UI/inputs/CleanInput';
import { UiTableInnerFiltersType } from 'src/components/UI/table/UiTable.helpers';
import { BaseTableFilterFiltersType } from 'src/models/Table';
import { StateFormReturnType } from 'src/utils/stateForm';
import { getSearchParams, setSearchParam } from 'src/utils/url';

const innerName = 'search' as const;

export type UISearchInputWithButtonType = { [innerName]: string | null };

type CleanProps = {
  formProps: StateFormReturnType;
  placeholder: string;

  // this might be called in UiTable getData when GET filters change
  onChange?: (filterObj: UICleanSearchInputWithButtonOnChangeType) => void;
  filterName?: string;
  doSetSearchParams?: boolean;
  inputTestId?: string;
  submitTestId?: string;
  v2?: boolean;
  withoutContainer?: boolean;
};

export type UICleanSearchInputProps = CleanProps;

export type UICleanSearchInputWithButtonOnChangeType = {
  Value: string | undefined;
  FilterType: 1;
  PropertyName: string;
};

export const UISearchInput: FC<CleanProps> = memo(
  ({
    formProps,
    placeholder,
    onChange,
    filterName = 'Name',
    doSetSearchParams = true,
    inputTestId,
    submitTestId,
    v2,
    withoutContainer,
  }) => {
    const { setValue, getValue } = formProps;

    /** set the value of the property (if it isn't set yet) */
    useEffect(() => {
      const filters = get(getSearchParams(), 'Filters', []) as BaseTableFilterFiltersType;
      const found = filters.filter(({ PropertyName }) => PropertyName === filterName);

      if (found.length) {
        setValue(filterName, found[0].Value);
      }
    }, [filterName, setValue]);

    const onLocalSubmit = useCallback(() => {
      const inputValue: string | null = getValue(filterName)?.replace(/\s+/, ' ').trim();

      if (inputValue && doSetSearchParams) {
        const filters = get(getSearchParams(), 'Filters', []) as BaseTableFilterFiltersType;

        setSearchParam<Partial<UiTableInnerFiltersType>>({
          Filters: [
            ...filters.filter(({ PropertyName }) => PropertyName !== filterName),
            {
              Value: inputValue,
              FilterType: 1,
              PropertyName: filterName,
            },
          ],
          PageIndex: 1,
        });
      }

      if (isFunction(onChange)) {
        onChange({
          Value: inputValue,
          FilterType: 1,
          PropertyName: filterName,
        });
      }
    }, [getValue, filterName, doSetSearchParams, onChange]);

    const onInputChange = useCallback(
      (v: string | null) => {
        /** react on clearing the input */
        if (v === null) {
          setValue(filterName, null);

          if (doSetSearchParams) {
            const params = getSearchParams();

            const filters = get(params, 'Filters', []) as BaseTableFilterFiltersType;

            setSearchParam<Partial<UiTableInnerFiltersType>>(
              {
                ...params,
                Filters: filters.filter(({ PropertyName }) => PropertyName !== filterName),
              },
              true
            );
          }

          if (isFunction(onChange)) {
            onChange({
              Value: undefined,
              FilterType: 1,
              PropertyName: filterName,
            });
          }
        }
      },
      [doSetSearchParams, filterName, onChange, setValue]
    );

    const component = useMemo(
      () => (
        <div
          className={classNames({
            'common-search': !v2,
            'common-search-v2': v2,
          })}
        >
          <UiCleanInput
            formProps={formProps}
            name={filterName}
            placeholder={placeholder}
            className={v2 ? undefined : 'text-uppercase-placeholder'}
            onEnter={onLocalSubmit}
            onChange={onInputChange}
            dataTestId={inputTestId}
            showAmount={false}
            prefix={
              v2 ? (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    /* eslint-disable-next-line max-len */
                    d="M19.3608 18.0381L14.6007 13.195C15.8246 11.7718 16.4952 9.981 16.4952 8.11673C16.4952 3.76105 12.8727 0.217285 8.42021 0.217285C3.96773 0.217285 0.345215 3.76105 0.345215 8.11673C0.345215 12.4724 3.96773 16.0162 8.42021 16.0162C10.0917 16.0162 11.6846 15.523 13.0465 14.5867L17.8427 19.4665C18.0431 19.6702 18.3128 19.7825 18.6017 19.7825C18.8752 19.7825 19.1347 19.6805 19.3316 19.495C19.7501 19.1011 19.7635 18.4478 19.3608 18.0381ZM8.42021 2.27801C11.7113 2.27801 14.3887 4.89719 14.3887 8.11673C14.3887 11.3363 11.7113 13.9555 8.42021 13.9555C5.12912 13.9555 2.45173 11.3363 2.45173 8.11673C2.45173 4.89719 5.12912 2.27801 8.42021 2.27801Z"
                    fill="#272756"
                  />
                </svg>
              ) : undefined
            }
            defaultPlaceholderStyle={v2}
            material={v2}
          />
          {!v2 && (
            <UiButton tag="a" className="search-button" onClick={onLocalSubmit} dataTestId={submitTestId}>
              <i className="material-icons">search</i>
            </UiButton>
          )}
        </div>
      ),
      [filterName, formProps, inputTestId, onInputChange, onLocalSubmit, placeholder, submitTestId, v2]
    );

    if (withoutContainer) {
      return component;
    }

    return (
      <div
        className={classNames('r-search-with-btn', {
          'filters-container': !v2,
          'filters-container-v2': v2,
        })}
      >
        {component}
      </div>
    );
  }
);
